import React from "react";
import { Text } from "@chakra-ui/react";
import { Link } from "react-router-dom";

const CONTACT_EMAIL = "mdeniz.info@gmail.com";
const EFFECTIVE_DATE = "October 21, 2026";
const APP_NAME = "Scale For Grams - AI Estimate";

function PrivacyPolicy() {
  const App = () => <span style={{ fontWeight: "bold" }}>{APP_NAME}</span>;

  const H = ({ children, id }) => (
    <Text fontSize="l" color="#fff" fontWeight="bold" mt={10} mb={5} id={id}>
      {children}
    </Text>
  );
  const SH = ({ children }) => (
    <Text fontSize="m" color="#fff" fontWeight="bold" mt={6} mb={3}>
      {children}
    </Text>
  );
  const P = ({ children }) => (
    <Text fontSize="m" color="#fff" mt={5} mb={5}>
      {children}
    </Text>
  );
  const Li = ({ children }) => (
    <Text fontSize="m" color="#fff" mt={3} mb={3} ml={4}>
      • {children}
    </Text>
  );
  const Ext = ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "underline", color: "#fff" }}
    >
      {children}
    </a>
  );

  return (
    <div className="container">
      <div className="header">
        <div className="header__right">
          <a href="/">
            <Text fontWeight="black" color="#fff" fontSize={20}>
              Muhammed Deniz
            </Text>
          </a>
        </div>

        <div className="header__left">
          <Link to="/home">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Home
            </Text>
          </Link>

          <Link to="/aiscaleforgrams/privacypolicy">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Privacy Policy
            </Text>
          </Link>

          <Link to="/aiscaleforgrams/termsofuse">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Terms Of Use
            </Text>
          </Link>
        </div>
      </div>

      <div style={{ margin: 60, marginLeft: "10%", marginRight: "15%" }}>
        <Text fontSize="xl" mb={5} fontWeight="black" color="#fff">
          Privacy Policy
        </Text>

        <P>
          Your privacy is important to us. It is Muhammed Deniz's policy to
          respect your privacy regarding any information we may collect from you
          through our app, <App />.
        </P>

        <P>
          We only ask for personal information when we truly need it to provide
          a service to you. We collect it by fair and lawful means, with your
          knowledge and consent. We also let you know why we’re collecting it
          and how it will be used.
        </P>

        <P>
          We only retain collected information for as long as necessary to
          provide you with your requested service. What data we store, we’ll
          protect within commercially acceptable means to prevent loss and
          theft, as well as unauthorised access, disclosure, copying, use or
          modification.
        </P>

        <P>
          We don’t sell your personal information, and we don’t share any
          personally identifying information publicly or with third parties,
          except as described in this policy (for example, the AI and service
          providers that make the app work) or when required to by law.
        </P>

        <P>
          Our app may link to external sites that are not operated by us. Please
          be aware that we have no control over the content and practices of
          these sites, and cannot accept responsibility or liability for their
          respective privacy policies.
        </P>

        <P>
          Your continued use of our app will be regarded as acceptance of our
          practices around privacy and personal information. If you have any
          questions about how we handle user data and personal information, feel
          free to contact us at{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>.
        </P>

        <P>This policy is effective as of {EFFECTIVE_DATE}.</P>

        {/* ------------------------------------------------------------ */}
        <H id="information-we-collect">1. Information We Collect and How</H>

        <P>
          <App /> uses artificial intelligence to estimate the weight, length or
          count of an object, or the amount of a cooking ingredient, from a
          photo you take or choose. It does not require you to create an
          account, and we do not ask for your name, email address, phone number,
          contacts or location. The information below is what the app and our
          service providers handle.
        </P>

        <SH>Photos you choose to submit</SH>
        <Li>
          The photo you take or select, together with the details sent along
          with it, is collected only when you tap the button to start an
          analysis. These details are the measurement type you chose (weight,
          length or count), the app language and unit system you use (metric or
          imperial), and, in Recipe Mode, the ingredient name and target amount
          you typed.
        </Li>
        <Li>
          The photo is intended to show an object or an ingredient, but it can
          accidentally include other things, such as a person, a face, a room or
          a document. Please avoid including people or private information in
          your photos.
        </Li>
        <Li>
          Before the first time a photo is sent to a third-party AI service, the
          app asks for your explicit permission (see “Consent and How to
          Withdraw It”). The app does not send a photo without that permission.
        </Li>
        <Li>
          The app only accesses the photos you choose through the camera or the
          system photo picker. It does not browse your photo library, and it
          does not save photos to your library.
        </Li>

        <SH>Anonymous app identifier and purchase information</SH>
        <Li>
          A random, anonymous app user ID created by RevenueCat, our
          subscription provider. It is used to check your subscription status
          and restore your purchases. It is not linked to your name or email.
        </Li>
        <Li>
          Purchase and subscription information (product, status, dates)
          processed by Apple App Store / Google Play and RevenueCat. We never
          receive your payment card details.
        </Li>

        <SH>Technical information</SH>
        <Li>
          Like most online services, the AI provider receives standard technical
          information when the app sends a request, such as your IP address,
          request time and basic device or network details. This is used to
          deliver the service, secure it and prevent abuse.
        </Li>
        <Li>
          If you email us, we receive your email address and what you write.
        </Li>

        <SH>Data kept only on your device</SH>
        <Li>
          Your analysis history (the photos you submitted and the results), your
          settings (language, unit system, theme), your AI consent choice and
          your subscription status are stored locally on your device. They are
          removed when you delete the app, and you can delete history items at
          any time inside the app.
        </Li>

        <SH>What we do not collect</SH>
        <Li>
          We do not collect your name, email address, phone number, contacts or
          location through the app, and we do not use advertising or third-party
          analytics SDKs in the app.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="how-we-use">2. How We Use Information</H>
        <Li>To create and show you the AI estimate you request.</Li>
        <Li>To verify your subscription and provide paid features.</Li>
        <Li>To keep the service secure and to prevent fraud and abuse.</Li>
        <Li>To maintain and improve the app.</Li>
        <Li>To respond to your support requests.</Li>
        <P>
          We do not use your photos for advertising, marketing, profiling,
          identification, authentication, or data mining, and we do not allow
          our providers to do so on our behalf. We do not use your photos to
          train AI models. Data collected for one purpose is not reused for a
          different purpose without your further consent, unless the law permits
          it.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="third-party-ai">3. Third-Party AI and Service Providers</H>

        <P>
          The photo you choose to analyze and the details sent with it are sent
          over an encrypted connection to the third-party AI service below, and
          the estimate is returned to your device. We only do this after you
          give permission in the app.
        </P>

        <SH>AI analysis</SH>
        <Li>
          OpenAI, L.L.C. receives your photo and the details described above and
          processes them with its AI models only to produce the estimate you
          requested.
        </Li>
        <Li>
          Privacy policy:{" "}
          <Ext href="https://openai.com/policies/privacy-policy">
            https://openai.com/policies/privacy-policy
          </Ext>
        </Li>

        <SH>Other service providers</SH>
        <Li>
          RevenueCat processes subscription status and purchases:{" "}
          <Ext href="https://www.revenuecat.com/privacy/">
            https://www.revenuecat.com/privacy/
          </Ext>
        </Li>
        <Li>
          Apple (App Store) and Google (Google Play) process payments and
          subscriptions under their own terms.
        </Li>

        <P>
          We share data only with providers needed to operate the app, and we
          select providers that give the same or an equal level of protection of
          user data as described in this policy and required by the App Store
          Review Guidelines. These providers process your data under their own
          privacy policies, including how long they retain it, so please review
          them. We do not sell, rent or share your photos with any other third
          parties.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="photo-data-processing">4. Photo Data</H>

        <SH>What we collect</SH>
        <Li>
          The photo that you voluntarily take or select so the AI can estimate
          weight, length, count or ingredient amount.
        </Li>
        <Li>
          We only process the photo itself. We do not create, extract or store
          biometric identifiers or face templates, and we do not use face
          mapping or depth data.
        </Li>

        <SH>What we do with it</SH>
        <Li>The photo is used only to provide the estimate you asked for.</Li>
        <Li>
          It is not used for identification, authentication, advertising,
          marketing, profiling or data mining, by us or by third parties.
        </Li>
        <Li>
          Processing happens in near real time when you request an analysis.
        </Li>

        <SH>Where it goes and how long it stays</SH>
        <Li>
          Your photo is sent to the AI provider in section 3 to get the
          estimate. We do not keep a copy of it. The result is returned to your
          device and saved only in your on-device history.
        </Li>
        <Li>
          How long the AI provider keeps data is decided by them, not by us, and
          it can change at any time. We do not control it and cannot guarantee
          it. Please check the provider’s current privacy policy (linked in
          section 3) for how long it keeps data.
        </Li>

        <SH>Security</SH>
        <Li>
          All data transmission is encrypted using industry-standard protocols.
        </Li>
        <Li>
          We apply appropriate technical and organizational measures to protect
          your data, such as limiting access to the systems that handle your
          requests.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="consent">5. Consent and How to Withdraw It</H>
        <Li>
          Before your photo is sent to a third-party AI service for the first
          time, the app clearly shows which service receives it and asks for
          your explicit permission (Allow / Don’t Allow). If you choose Don’t
          Allow, no photo is sent and the AI analysis cannot be used.
        </Li>
        <Li>
          You can withdraw this permission at any time in the app under Settings
          → AI Photo Analysis (switch it off). After that, the app asks again
          before sending your next photo. You can also stop using the AI
          features or delete the app.
        </Li>
        <Li>
          You choose which photo to submit. Processing happens only when you
          request an analysis.
        </Li>
        <Li>
          You can revoke camera and photo access at any time in your device
          settings.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="retention">6. Data Retention and Deletion</H>
        <Li>
          We do not keep copies of your photos on our servers. Data on your
          device stays until you delete the history items, delete the app or its
          data.
        </Li>
        <Li>
          Standard technical logs kept by the AI provider are retained under its
          own policy.
        </Li>
        <Li>
          Purchase and subscription data is kept by RevenueCat, Apple and Google
          under their retention policies.
        </Li>
        <P>
          Because the app has no accounts, we cannot identify you from your
          device. To request deletion of data connected to you, or to ask
          questions about it, email{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext> with
          details about your use (for example your anonymous RevenueCat ID if
          you have it, and approximate dates). We will make reasonable efforts
          to find and delete the data connected to your request, and to ask our
          providers to do the same where applicable.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="children">7. Children</H>
        <P>
          <App /> is not directed to children under 13 (or under 16 where local
          law requires a higher age). We do not knowingly collect personal
          information from children. Please do not submit photos that show
          children. If you believe a child’s information has been submitted,
          contact us and we will take steps to remove it.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="tracking">8. Tracking and Advertising</H>
        <Li>
          We do not show advertising in the app, we do not sell your personal
          information, and the app does not track you across other companies’
          apps or websites. For this reason, the app does not show the App
          Tracking Transparency prompt.
        </Li>
        <Li>Your photos are not used for advertising or marketing.</Li>

        {/* ------------------------------------------------------------ */}
        <H id="your-rights">9. Your Rights and Choices</H>
        <P>
          Depending on where you live (for example in the EU/EEA, UK or
          California), you may have the right to access, correct, delete or
          restrict the use of your personal data, to object to processing, to
          withdraw consent, and to complain to your local data protection
          authority. Where we rely on your consent (such as sending photos to
          the AI service), you can withdraw it at any time as described above.
          To exercise a right, contact us at{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>.
        </P>

        <H id="transfers">10. International Data Transfers</H>
        <P>
          Our providers may process data in countries other than your own,
          including the United States. By giving your permission in the app, you
          agree to this transfer, which is subject to the safeguards and
          policies of those providers.
        </P>

        <H id="changes">11. Changes to This Policy</H>
        <P>
          We may update this policy from time to time. The latest version is
          always available at this page, with the effective date shown above. If
          we make material changes to how your data is used, we will ask for
          your permission again in the app where required.
        </P>

        <H id="contact">12. Contact</H>
        <P>
          Questions or requests:{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>
        </P>

        <H id="terms">13. Terms of Use</H>
        <P>
          Your use of <App /> is also subject to our{" "}
          <Link
            to="/aiscaleforgrams/termsofuse"
            style={{ textDecoration: "underline", color: "#fff" }}
          >
            Terms of Use
          </Link>
          , which cover the license, subscriptions, acceptable use, disclaimers
          and governing law.
        </P>

        <Text fontSize="s" color="#fff" mt={10} textAlign="right">
          Last updated: {EFFECTIVE_DATE}
        </Text>

        <Text
          fontSize="xl"
          color="#fff"
          fontWeight="hairline"
          mt={20}
          mb={0}
          align="right"
        >
          Muhammed Deniz
        </Text>

        <Text
          fontSize="s"
          color="#fff"
          fontWeight="hairline"
          mt={0}
          mb={2}
          align="right"
        >
          <App />
        </Text>

        <Text fontSize="s" color="#fff" mb={20} align="right">
          Contact: <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>
        </Text>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
